<template>
  <!-- The sky (quest journey 2026-10-27): seven constellations from the bottom
       up, drawn by quest-sky.js's drawSky (the prototype's drawings). Every
       star, along-the-way star and Saturn opens its sheet. -->
  <div class="qsky" :class="{ rm: reduced }" data-testid="quest-sky" v-html="svg" @click="onClick" @keydown.enter.prevent="onClick" @keydown.space.prevent="onClick"></div>
</template>

<script setup>
import { computed } from 'vue'
import { drawSky } from '@/utils/quest-sky'

const props = defineProps({
  journey: { type: Object, required: true },
  // { bright, ceremony, ring, liftWord, ringDone }
  fx: { type: Object, default: () => ({}) },
  reduced: { type: Boolean, default: false },
  pfx: { type: String, default: 'qj' },
})
const emit = defineEmits(['star'])
const svg = computed(() => drawSky(props.journey, props.fx, props.pfx))

function onClick (e) {
  const t = e.target && e.target.closest ? e.target.closest('[data-key]') : null
  if (t) emit('star', t.getAttribute('data-key'))
}
</script>

<style>
/* The sky's type and motion (prototype.html "sky"), for the v-html drawing. */
.qsky svg.sky { display: block; width: 100%; height: auto; user-select: none; -webkit-tap-highlight-color: transparent; }
.qsky .dsp { font-family: Cinzel, serif; font-weight: 600; }
.qsky .srf { font-family: 'Cormorant Garamond', Cormorant, serif; }
.qsky .it-s { font-family: 'Cormorant Garamond', Cormorant, serif; font-style: italic; }
.qsky .st { cursor: pointer; }
.qsky .st:focus { outline: none; }
.qsky .st:focus-visible .hit { stroke: #ffe7b5; stroke-width: 1.2; }
.qsky .pulse { animation: qsky-pulse 2.4s ease-in-out infinite; }
@keyframes qsky-pulse { 0%, 100% { opacity: 1; } 50% { opacity: .3; } }
.qsky .blk.bright { animation: qsky-bright 1.3s ease-out both; animation-delay: var(--d); }
@keyframes qsky-bright { 0% { filter: none; } 40% { filter: brightness(1.9) drop-shadow(0 0 6px #ffe7b5); } 100% { filter: none; } }
.qsky .ringagain { transform-box: fill-box; transform-origin: center; animation: qsky-ring 1.7s ease-out both; }
.qsky .ringagain.r2 { animation-delay: .55s; }
@keyframes qsky-ring { 0% { transform: scale(.4); opacity: .95; } 100% { transform: scale(3.4); opacity: 0; } }
.qsky .lift { animation: qsky-lift 3s ease-out .3s both; }
@keyframes qsky-lift { 0% { opacity: 0; transform: translateY(0); } 15% { opacity: 1; } 75% { opacity: 1; transform: translateY(-40px); } 100% { opacity: 0; transform: translateY(-56px); } }
/* The Sun's ceremony in the sky. */
.qsky .cer .sunup { animation: qsky-sunup 2s cubic-bezier(.2,.7,.2,1) .3s both; }
@keyframes qsky-sunup { from { transform: translateY(110px); } }
.qsky .cer .split { stroke-dasharray: 100; animation: qsky-draw .5s ease-in 2.2s both; }
.qsky .cer .halfL { animation: qsky-partL .9s ease-in 2.75s both; }
.qsky .cer .halfR { animation: qsky-partR .9s ease-in 2.75s both; }
@keyframes qsky-partL { to { transform: translateX(-46px); opacity: 0; } }
@keyframes qsky-partR { to { transform: translateX(46px); opacity: 0; } }
.qsky .cer .rays { animation: qsky-fade .8s ease-out 3.3s both; }
.qsky .cer .sunstars { animation: qsky-fade .8s ease-out 3.6s both; }
.qsky .halfL, .qsky .halfR, .qsky .sunstars, .qsky .rays { transform-box: view-box; }
@keyframes qsky-draw { from { stroke-dashoffset: 100; } }
@keyframes qsky-fade { from { opacity: 0; } }
@keyframes qsky-still { 0% { opacity: 0; } 15%, 80% { opacity: 1; } 100% { opacity: 0; } }
/* Reduced motion: in place, fades only. */
.qsky.rm .blk, .qsky.rm .cer *, .qsky.rm .pulse { animation: none !important; }
.qsky.rm .ringagain { display: none; }
.qsky.rm .lift { animation: qsky-still 3s ease-in-out both !important; }
.qsky.rm .cer .halfL, .qsky.rm .cer .halfR, .qsky.rm .cer .split { display: none; }
@media (prefers-reduced-motion: reduce) {
  .qsky .blk, .qsky .cer *, .qsky .pulse { animation: none !important; }
  .qsky .ringagain { display: none; }
  .qsky .lift { animation: qsky-still 3s ease-in-out both !important; }
  .qsky .cer .halfL, .qsky .cer .halfR, .qsky .cer .split { display: none; }
}
</style>
